/**
 * Edit Shareholder Modal
 *
 * Modal for editing shareholder information.
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/ui/button";
import { useUpdateShareholder } from "@/hooks/use-share-book";
import type { ShareholderWithHoldings } from "@/lib/api";

interface EditShareholderModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  shareholder: ShareholderWithHoldings;
}

const shareholderSchema = z.object({
  name: z.string().min(1, "Namn krävs"),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  email: z.string().email("Ogiltig e-postadress").optional().or(z.literal("")),
  phone: z.string().optional(),
  hidePersonalId: z.boolean().optional(),
  hasGuardian: z.boolean().optional(),
  guardianName: z.string().optional(),
  guardianPersonalId: z.string().optional(),
  guardianAddress: z.string().optional(),
  notes: z.string().optional(),
});

type ShareholderFormData = z.infer<typeof shareholderSchema>;

export function EditShareholderModal({
  isOpen,
  onClose,
  companyId,
  shareholder,
}: EditShareholderModalProps) {
  const updateShareholder = useUpdateShareholder();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ShareholderFormData>({
    resolver: zodResolver(shareholderSchema),
    defaultValues: {
      name: shareholder.name,
      address: shareholder.address || "",
      postalCode: shareholder.postalCode || "",
      city: shareholder.city || "",
      country: shareholder.country || "Sverige",
      email: shareholder.email || "",
      phone: shareholder.phone || "",
      hidePersonalId: shareholder.hidePersonalId || false,
      hasGuardian: shareholder.hasGuardian || false,
      guardianName: shareholder.guardianName || "",
      guardianPersonalId: shareholder.guardianPersonalId || "",
      guardianAddress: shareholder.guardianAddress || "",
      notes: shareholder.notes || "",
    },
  });

  const hasGuardian = watch("hasGuardian");
  const isPerson = shareholder.type === "person";

  // Reset form when shareholder changes
  useEffect(() => {
    reset({
      name: shareholder.name,
      address: shareholder.address || "",
      postalCode: shareholder.postalCode || "",
      city: shareholder.city || "",
      country: shareholder.country || "Sverige",
      email: shareholder.email || "",
      phone: shareholder.phone || "",
      hidePersonalId: shareholder.hidePersonalId || false,
      hasGuardian: shareholder.hasGuardian || false,
      guardianName: shareholder.guardianName || "",
      guardianPersonalId: shareholder.guardianPersonalId || "",
      guardianAddress: shareholder.guardianAddress || "",
      notes: shareholder.notes || "",
    });
  }, [shareholder, reset]);

  const onSubmit = async (data: ShareholderFormData) => {
    try {
      await updateShareholder.mutateAsync({
        shareholderId: shareholder.id,
        companyId,
        data: {
          name: data.name,
          address: data.address || undefined,
          postalCode: data.postalCode || undefined,
          city: data.city || undefined,
          country: data.country || undefined,
          email: data.email || undefined,
          phone: data.phone || undefined,
          hidePersonalId: data.hidePersonalId || false,
          hasGuardian: data.hasGuardian || false,
          guardianName: data.guardianName || undefined,
          guardianPersonalId: data.guardianPersonalId || undefined,
          guardianAddress: data.guardianAddress || undefined,
          notes: data.notes || undefined,
        },
      });
      toast.success("Aktieägaren har uppdaterats");
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Kunde inte uppdatera aktieägaren";
      toast.error(errorMessage);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Redigera aktieägare" size="lg">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Read-only info */}
        <div className="p-4 bg-gray-50 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Typ:</span>
            <span className="text-gray-900 font-medium">
              {shareholder.type === "person"
                ? "Fysisk person"
                : shareholder.type === "company"
                ? "Juridisk person"
                : shareholder.type === "estate"
                ? "Dödsbo"
                : "Stiftelse/Trust"}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">
              {isPerson ? "Personnummer:" : "Organisationsnummer:"}
            </span>
            <span className="text-gray-900 font-medium">
              {isPerson
                ? shareholder.hidePersonalId
                  ? "●●●●●●-●●●●"
                  : shareholder.personalId || "-"
                : shareholder.orgNumber || "-"}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Typ och identitetsnummer kan inte ändras. Kontakta support vid behov.
          </p>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Namn *
          </label>
          <input
            type="text"
            {...register("name")}
            className="input"
            placeholder={isPerson ? "Förnamn Efternamn" : "Företagsnamn AB"}
          />
          {errors.name && (
            <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
          )}
        </div>

        {/* Hide personal ID option (for persons) */}
        {isPerson && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register("hidePersonalId")}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label className="text-sm text-gray-700">
              Dölj personnummer i utskrift
            </label>
          </div>
        )}

        {/* Address */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Postadress
          </label>
          <input
            type="text"
            {...register("address")}
            className="input"
            placeholder="Gatuadress"
          />
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Postnummer
            </label>
            <input
              type="text"
              {...register("postalCode")}
              className="input"
              placeholder="123 45"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ort
            </label>
            <input
              type="text"
              {...register("city")}
              className="input"
              placeholder="Stockholm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Land
            </label>
            <input
              type="text"
              {...register("country")}
              className="input"
              placeholder="Sverige"
            />
          </div>
        </div>

        {/* Contact */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              E-post
            </label>
            <input
              type="email"
              {...register("email")}
              className="input"
              placeholder="namn@exempel.se"
            />
            {errors.email && (
              <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefon
            </label>
            <input
              type="tel"
              {...register("phone")}
              className="input"
              placeholder="070-123 45 67"
            />
          </div>
        </div>

        {/* Guardian section (for minors) */}
        {isPerson && (
          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                {...register("hasGuardian")}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Aktieägaren har god man/förmyndare
              </span>
            </label>

            {hasGuardian && (
              <div className="space-y-4 pl-6 border-l-2 border-blue-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    God man/förmyndare namn
                  </label>
                  <input
                    type="text"
                    {...register("guardianName")}
                    className="input"
                    placeholder="Förnamn Efternamn"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Personnummer
                  </label>
                  <input
                    type="text"
                    {...register("guardianPersonalId")}
                    className="input"
                    placeholder="YYYYMMDD-XXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Adress
                  </label>
                  <input
                    type="text"
                    {...register("guardianAddress")}
                    className="input"
                    placeholder="Fullständig postadress"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Anteckningar
          </label>
          <textarea
            {...register("notes")}
            className="input min-h-[80px]"
            placeholder="Interna anteckningar om aktieägaren..."
          />
        </div>

        {/* Holdings summary (read-only) */}
        <div className="border-t border-gray-200 pt-4">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Innehav</h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Totalt antal aktier:</span>
              <span className="font-medium text-gray-900">
                {shareholder.totalShares.toLocaleString("sv-SE")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Ägarandel:</span>
              <span className="font-medium text-gray-900">
                {shareholder.ownershipPercent.toFixed(2)}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Röster:</span>
              <span className="font-medium text-gray-900">
                {shareholder.totalVotes.toLocaleString("sv-SE")}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Röstandel:</span>
              <span className="font-medium text-gray-900">
                {shareholder.votingPercent.toFixed(2)}%
              </span>
            </div>
          </div>
          {shareholder.holdings.length > 0 && (
            <div className="mt-3 space-y-2">
              {shareholder.holdings.map((holding, idx) => (
                <div
                  key={idx}
                  className="p-2 bg-gray-50 rounded text-xs flex justify-between"
                >
                  <span className="text-gray-600">
                    {holding.shareClass.name} ({holding.shareClass.code})
                  </span>
                  <span className="text-gray-900">
                    {holding.totalShares.toLocaleString("sv-SE")} st • nr{" "}
                    {holding.shareNumbers}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="outline" onClick={handleClose}>
            Avbryt
          </Button>
          <Button type="submit" disabled={isSubmitting || !isDirty}>
            {isSubmitting ? (
              <>
                <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                Sparar...
              </>
            ) : (
              "Spara ändringar"
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
